import { Injectable, inject } from '@angular/core';
import { AuthService } from './auth.service';

@Injectable({
    providedIn: 'root'
})
export class PermissionService {
    private authService = inject(AuthService);
    private static readonly USER_KEYWORDS = ['user', 'users', 'usuario', 'usuarios'];
    private static readonly LABORATORY_KEYWORDS = ['laboratory', 'laboratories', 'laboratorio', 'laboratorios'];
    private static readonly USER_READ_ACTIONS = ['read', 'get', 'list', 'view', '*'];
    private static readonly USER_WRITE_ACTIONS = ['create', 'post', 'put', 'patch', 'update', 'delete', 'write', '*'];

    /**
     * Checks if the current user has a specific permission
     */
    hasPermission(permission: string): boolean {
        if (this.authService.isSuperUser()) {
            return true;
        }
        const user = this.authService.currentUser();
        if (!user || !user.permissions) {
            return false;
        }
        return user.permissions.some((grantedPermission) => this.matchesPermission(grantedPermission, permission));
    }

    /**
     * Checks if the current user has access to a specific module/section/page
     */
    canAccess(module: string, section: string, page: string): boolean {
        const permission = `permission:${module.toLowerCase()}:${section.toLowerCase()}:${page.toLowerCase()}:read`;
        return this.hasPermission(permission);
    }

    canViewUsers(): boolean {
        if (this.authService.isRbiAdmin() || this.authService.isSuperUser()) {
            return true;
        }

        return this.hasPermissionByKeywords(
            PermissionService.USER_KEYWORDS,
            PermissionService.USER_READ_ACTIONS,
        ) || this.canManageUsers();
    }

    canManageUsers(): boolean {
        if (this.authService.isRbiAdmin() || this.authService.isSuperUser()) {
            return true;
        }

        return this.hasPermissionByKeywords(
            PermissionService.USER_KEYWORDS,
            PermissionService.USER_WRITE_ACTIONS,
        );
    }

    /**
     * Permite administrar los laboratorios de otros usuarios.
     * Sigue el mismo criterio que el resto: un administrador puede todo y,
     * si no lo es, se resuelve por los permisos del usuario.
     */
    canManageLaboratories(): boolean {
        if (this.authService.isRbiAdmin() || this.authService.isSuperUser()) {
            return true;
        }

        return this.hasPermissionByKeywords(
            PermissionService.LABORATORY_KEYWORDS,
            PermissionService.USER_WRITE_ACTIONS,
        );
    }

    private matchesPermission(grantedPermission: string, requestedPermission: string): boolean {
        if (grantedPermission === requestedPermission) {
            return true;
        }

        const grantedParts = grantedPermission.split(':');
        const requestedParts = requestedPermission.split(':');
        if (grantedParts.length !== requestedParts.length) {
            return false;
        }

        return grantedParts.every((part, index) => part === '*' || part === requestedParts[index]);
    }

    private hasPermissionByKeywords(keywords: string[], actions: string[]): boolean {
        const user = this.authService.currentUser();
        const grantedPermissions = user?.permissions ?? [];
        if (grantedPermissions.length === 0) {
            return false;
        }

        const normalizedKeywords = new Set(keywords.map((item) => item.toLowerCase()));
        const normalizedActions = new Set(actions.map((item) => this.normalizeAction(item)));

        return grantedPermissions.some((permission) => {
            const tokens = this.tokenizePermission(permission);
            if (tokens.length === 0) {
                return false;
            }

            const actionToken = this.normalizeAction(tokens[tokens.length - 1]);
            const actionMatches = actionToken === '*' || normalizedActions.has(actionToken);
            if (!actionMatches) {
                return false;
            }

            return tokens.some((token) => normalizedKeywords.has(token) || token === '*');
        });
    }

    private tokenizePermission(permission: string): string[] {
        return permission
            .toLowerCase()
            .split(/[:._/\\-]+/)
            .map((token) => token.trim())
            .filter((token) => token.length > 0);
    }

    private normalizeAction(action: string): string {
        const normalized = action.toLowerCase().trim();
        switch (normalized) {
            case 'get':
                return 'read';
            case 'post':
                return 'create';
            case 'put':
            case 'patch':
                return 'update';
            default:
                return normalized;
        }
    }
}
